import React, { createElement, useEffect, useState } from "react";
import type { PropsWithChildren } from "react";
import { ExpandMore, Refresh } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Collapse, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";

const isDev = import.meta.env.DEV;
const isTest = import.meta.env.MODE === "test" || process.env.NODE_ENV === "test";
const isPlaywright = typeof window !== "undefined" && window["REACT_APP_PLAYWRIGHT_RUN"] === "true";

function getErrorType(error: Error): { type: string } {
  const message = error.message;
  if (message.includes("does not provide an export named")) {
    return { type: "Import Error" };
  }
  if (message.includes("Cannot read properties of") || message.includes("Cannot read property")) {
    return { type: "Null Reference" };
  }
  if (message.includes("is not a function")) {
    return { type: "Function Error" };
  }
  if (message.includes("NetworkError") || message.includes("fetch")) {
    return { type: "Network Error" };
  }
  return { type: "Runtime Error" };
}

function getCompactStack(stack: string): string {
  // Split stack into lines and filter out node_modules and internal React lines
  const lines = stack.split("\n").filter((line) => {
    const isNodeModule = line.includes("node_modules");
    const isReactInternal = line.includes("react-dom") || line.includes("react-reconciler");
    const isViteInternal = line.includes("vite") || line.includes("chunk-");
    return !isNodeModule && !isReactInternal && !isViteInternal;
  });

  // Take first 3 relevant lines
  return lines.slice(0, 3).join("\n");
}

function DevErrorFallback({
  error,
  componentStack,
  resetError,
}: {
  error: any;
  componentStack: string;
  resetError: () => void;
}) {
  const [showStack, setShowStack] = useState(false);
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const { type } = getErrorType(errorObj);
  const compactStack = getCompactStack(errorObj.stack || "");

  // For Playwright tests, log in a format that will be visible in test output
  if (isPlaywright) {
    console.log(`Runtime Error: ${errorObj.message}\n\nStack:\n${compactStack}`);
  }

  const copyError = () => {
    const report = `${type}: ${errorObj.message}\n\nStack:\n${compactStack}`;
    void navigator.clipboard?.writeText(report);
  };

  return (
    <Alert
      severity="error"
      sx={{
        m: 1,
        fontSize: "0.875rem",
        bgcolor: "error.main",
        color: "error.contrastText",
        "& .MuiAlert-icon": { color: "error.contrastText" },
      }}
      action={
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button
            size="small"
            onClick={resetError}
            startIcon={<Refresh />}
            sx={{ color: "inherit" }}
            data-testid="retry-button"
          >
            Retry
          </Button>
          <Button size="small" onClick={copyError} sx={{ color: "inherit" }}>
            Copy
          </Button>
          <Button
            size="small"
            onClick={() => setShowStack(!showStack)}
            endIcon={<ExpandMore sx={{ transform: showStack ? "rotate(180deg)" : "none" }} />}
            sx={{ color: "inherit" }}
            data-testid="toggle-details-button"
          >
            Details
          </Button>
        </Box>
      }
      data-testid="enhanced-error-fallback"
    >
      <Typography variant="subtitle2" gutterBottom>
        {type}: {errorObj.message}
      </Typography>
      <Box
        component="pre"
        sx={{
          fontSize: "0.75rem",
          mt: 1,
          p: 1,
          bgcolor: "rgba(255,255,255,0.1)",
          borderRadius: 1,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {compactStack}
      </Box>
      <Collapse in={showStack}>
        <Box
          component="pre"
          sx={{
            fontSize: "0.75rem",
            overflow: "auto",
            maxHeight: "200px",
            mt: 1,
            p: 1,
            bgcolor: "rgba(255,255,255,0.1)",
            borderRadius: 1,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          <strong>Full Stack:</strong>
          {"\n"}
          {errorObj.stack}
          {"\n\n"}
          <strong>Component Stack:</strong>
          {"\n"}
          {componentStack}
        </Box>
      </Collapse>
    </Alert>
  );
}

function DefaultErrorFallback({
  error,
  componentStack,
  resetError,
}: {
  error: any;
  componentStack: string;
  resetError: () => void;
}) {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const { type } = getErrorType(errorObj);
  const compactStack = getCompactStack(errorObj.stack || "");

  if (isPlaywright) {
    console.log(`Runtime Error: ${type}: ${errorObj.message}\n\nStack:\n${compactStack}`);
  }

  const message =
    "You have encountered an error. Try to reload the page and contact support if the problem is consistent.";
  const title = `${type}: ${errorObj.message}`;

  return (
    <Alert
      severity="error"
      variant="filled"
      action={
        <Button
          size="small"
          onClick={resetError}
          startIcon={<Refresh />}
          sx={{ color: "inherit" }}
          data-testid="retry-button"
        >
          Retry
        </Button>
      }
      sx={{ m: 1 }}
      data-testid="default-error-fallback"
    >
      <AlertTitle>{title}</AlertTitle>
      <Box
        component="pre"
        sx={{ fontSize: "0.75rem", mt: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {message}
      </Box>
    </Alert>
  );
}

export function ErrorBoundary({
  children,
  fallback,
  onError,
  ...props
}: PropsWithChildren<Sentry.ErrorBoundaryProps>): JSX.Element {
  const [error, setError] = useState<Error | null>(null);
  const [componentStack, setComponentStack] = useState<string>("");

  useEffect(() => {
    if (error && onError) {
      onError(error, componentStack);
    }
  }, [error, componentStack, onError]);

  const resetError = () => {
    setError(null);
    setComponentStack("");
  };

  if (error) {
    if (isDev || isTest) {
      return (
        <DevErrorFallback error={error} componentStack={componentStack} resetError={resetError} />
      );
    }
    return fallback ? (
      createElement(fallback, { error, componentStack, resetError })
    ) : (
      <DefaultErrorFallback error={error} componentStack={componentStack} resetError={resetError} />
    );
  }

  return (
    <Sentry.ErrorBoundary
      {...props}
      onError={(error, componentStack) => {
        setError(error);
        setComponentStack(componentStack);
      }}
      onReset={resetError}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export default ErrorBoundary;
